import { apiFetch, tokenStore } from "./client";

export interface ShareMedia {
  id: string;
  kind: "image" | "video";
  filename: string;
  mime_type: string;
  size_bytes: number;
  label: string | null;
  tags: string[];
  has_thumb: boolean;
  created_at: string;
}

export interface ShareText {
  id: string;
  title: string;
  body: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

interface MediaListResponse {
  success: boolean;
  media: ShareMedia[];
}
interface MediaSingleResponse {
  success: boolean;
  media: ShareMedia;
}
interface TextListResponse {
  success: boolean;
  text: ShareText[];
}
interface TextSingleResponse {
  success: boolean;
  text: ShareText;
}

// ---- Média ----
export async function listShareMedia(): Promise<ShareMedia[]> {
  const data = await apiFetch<MediaListResponse>("/api/share/media");
  return data.media;
}

export async function uploadShareMedia(
  file: File,
  meta: { label?: string; tags?: string } = {}
): Promise<ShareMedia> {
  const form = new FormData();
  form.append("file", file);
  if (meta.label) form.append("label", meta.label);
  if (meta.tags) form.append("tags", meta.tags);
  // apiFetch ne pose pas de Content-Type si le body n'est pas string -> le
  // navigateur met le bon multipart/form-data boundary lui-même.
  const data = await apiFetch<MediaSingleResponse>("/api/share/media", {
    method: "POST",
    body: form,
  });
  return data.media;
}

export async function deleteShareMedia(id: string): Promise<void> {
  await apiFetch(`/api/share/media/${id}`, { method: "DELETE" });
}

// URL d'aperçu de l'original (JWT passé en ?token= car <img>/<video> ne
// peuvent pas poser de header Authorization).
export function shareMediaRawUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  const token = tokenStore.get() ?? "";
  return `${base}/api/share/media/${id}/raw?token=${encodeURIComponent(token)}`;
}

// URL de la vignette légère (générée à l'upload) pour la grille du panel.
export function shareMediaThumbUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  const token = tokenStore.get() ?? "";
  return `${base}/api/share/media/${id}/thumb?token=${encodeURIComponent(token)}`;
}

// Télécharge un fichier (Bearer JWT) et déclenche la sauvegarde navigateur.
async function saveBlob(path: string, fallbackName: string): Promise<void> {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  const token = tokenStore.get();
  const res = await fetch(`${base}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();

  let name = fallbackName;
  const cd = res.headers.get("content-disposition");
  const m = cd && /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(cd);
  if (m) {
    try {
      name = decodeURIComponent(m[1]);
    } catch {
      name = m[1];
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Télécharge l'original (non modifié).
export function downloadShareMediaOriginal(media: ShareMedia): Promise<void> {
  return saveBlob(`/api/share/media/${media.id}/raw?download=1`, media.filename);
}

// Télécharge la variante nettoyée par media_ghost (anti-dédup, unique).
export function downloadShareMediaCleaned(media: ShareMedia): Promise<void> {
  return saveBlob(`/api/share/media/${media.id}/clean`, media.filename);
}

// ---- Texte ----
export async function listShareText(): Promise<ShareText[]> {
  const data = await apiFetch<TextListResponse>("/api/share/text");
  return data.text;
}

export async function createShareText(payload: {
  title: string;
  body: string;
  tags?: string[];
}): Promise<ShareText> {
  const data = await apiFetch<TextSingleResponse>("/api/share/text", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.text;
}

export async function updateShareText(
  id: string,
  payload: Partial<{ title: string; body: string; tags: string[] }>
): Promise<ShareText> {
  const data = await apiFetch<TextSingleResponse>(`/api/share/text/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.text;
}

export async function deleteShareText(id: string): Promise<void> {
  await apiFetch(`/api/share/text/${id}`, { method: "DELETE" });
}
